// The shapes a new challenge starts from: the three structures the platform
// sells, and the step every phase opens on. No JSX, no store.

import type { OrgWords } from '../core/ruleText';
import { KEPT_DAYS_PREFILL, type CustomChallengeBuilderInput, type StepBuilderInput } from '../form';
import { enableStandardRules, initializeAllRules } from '../core/ruleRegistry';

export type ChallengeMode = 'instant' | '1-phase' | '2-phase';

export const MODE_BY_STEP_COUNT: Record<number, ChallengeMode> = { 1: 'instant', 2: '1-phase', 3: '2-phase' };

export const MODE_LABELS: Record<ChallengeMode, { title: string; desc: string }> = {
  instant: { title: 'Instant funded', desc: 'No evaluation. Trading starts on the funded account.' },
  '1-phase': { title: 'One evaluation', desc: 'Pass one phase, then the funded account.' },
  '2-phase': { title: 'Two evaluations', desc: 'Pass two phases, then the funded account.' },
};

export function defaultStep(name: string, preEnableRules = true): StepBuilderInput {
  const rules = preEnableRules ? enableStandardRules(initializeAllRules()) : initializeAllRules();
  return {
    displayName: name,
    freshAccount: true,
    profitTargetPct: '8',
    profitTargetAbs: '',
    minTradingDays: '5',
    minProfitableDays: '',
    timeLimitDays: '30',
    tradingDayMode: 'opened_trade',
    minDailyProfit: '',
    consistencyMode: '',
    consistencyPct: '',
    consistencyBasis: 'total_net_profit',
    consistencyRemedy: 'fail_step',
    consistencyWindow: 'account_life',
    inactivityDays: '',
    inactivityClockFrom: 'purchase',
    inactivityOutcome: 'expire',
    payoutPeakPolicy: 'reduce_by_amount',
    challengeDayBoundaryTime: '17:00',
    challengeDayBoundaryTimezone: 'America/New_York',
    rules,
  };
}

export function stepsForMode(mode: ChallengeMode): StepBuilderInput[] {
  if (mode === 'instant') return [defaultStep('Funded')];
  if (mode === '1-phase') return [defaultStep('Evaluation'), defaultStep('Funded')];
  return [defaultStep('Evaluation'), { ...defaultStep('Verification'), profitTargetPct: '5' }, defaultStep('Funded')];
}

/** The terms a reset choice leaves: a reset needs the account open after the
 *  failure, so allowing resets moves an account closed at the failure to the
 *  pre-filled days; any other end stays as it is. */
export function withResets(
  form: Pick<CustomChallengeBuilderInput, 'failedAccountEnd' | 'failedAccountDays'>,
  allowed: boolean,
): Pick<CustomChallengeBuilderInput, 'resetAllowed' | 'failedAccountEnd' | 'failedAccountDays'> {
  return allowed && form.failedAccountEnd === 'close_now'
    ? { resetAllowed: true, failedAccountEnd: 'keep_days', failedAccountDays: KEPT_DAYS_PREFILL }
    : { resetAllowed: allowed, failedAccountEnd: form.failedAccountEnd, failedAccountDays: form.failedAccountDays };
}

/** A new template, named in the words of its org. */
export function emptyForm(words: Pick<OrgWords, 'run_label'>): CustomChallengeBuilderInput {
  return {
    displayName: `My ${words.run_label}`,
    description: '',
    accountSize: '50000',
    // A new template is priced in dollars.
    currency: 'USD',
    challengePrice: '39',
    leverage: '100',
    positionMode: 'netting',
    hedgeMarginMethod: 'gross',
    hedgeOffsetFactor: '0.30',
    resetAllowed: true,
    failedAccountEnd: 'keep_days',
    failedAccountDays: KEPT_DAYS_PREFILL,
    requiresActivation: true,
    activationPrice: '129',
    profitSplitPct: '80',
    feeRefundPct: '',
    billingPeriod: 'one_time',
    steps: stepsForMode('1-phase'),
  };
}

/** The label a phase carries in the tabs and the preview. */
export function phaseName(step: StepBuilderInput, index: number, total: number): string {
  if (step.displayName.trim()) return step.displayName.trim();
  if (index === total - 1 && total > 1) return 'Funded';
  return `Phase ${index + 1}`;
}
